import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  emailTemplatesApi,
  mailboxesApi,
  phoneNumbersApi,
  smsTemplatesApi
} from "./api";
import type {
  CreateEmailTemplatePayload,
  RequestPhoneNumberRequest,
  SmsTemplatePayload,
  UpdateEmailTemplatePayload
} from "./types";

const mailboxKeys = {
  all: ["MAILBOXES"],
  lists: () => [...mailboxKeys.all, "LIST"],
  list: (filters?: Record<string, unknown>) => [
    ...mailboxKeys.lists(),
    filters
  ],
  details: () => [...mailboxKeys.all, "DETAIL"],
  detail: (id: string) => [...mailboxKeys.details(), id]
};

const phoneNumberKeys = {
  all: ["PHONE_NUMBERS"],
  lists: () => [...phoneNumberKeys.all, "LIST"],
  propertyDefaults: () => [...phoneNumberKeys.all, "PROPERTY_DEFAULTS"],
  propertyDefault: (propertyId: number) => [
    ...phoneNumberKeys.all,
    "PROPERTY_DEFAULT",
    propertyId
  ]
};

export const useMailboxes = (enabled: boolean = true) =>
  useQuery({
    queryKey: mailboxKeys.lists(),
    queryFn: () => mailboxesApi.list(),
    enabled
  });

export const useSetDefault = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => mailboxesApi.setDefault(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mailboxKeys.lists() });
    }
  });
};

export const useRemoveDefault = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => mailboxesApi.removeDefault(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mailboxKeys.lists() });
    }
  });
};

// SMS Templates

const smsTemplateKeys = {
  all: ["SMS_TEMPLATES"],
  lists: () => [...smsTemplateKeys.all, "LIST"],
  list: (page: number, perPage: number) => [
    ...smsTemplateKeys.lists(),
    page,
    perPage
  ],
  variables: (subjectType: string) => [
    ...smsTemplateKeys.all,
    "VARIABLES",
    subjectType
  ]
};

export const useSmsTemplates = (page = 1, perPage = 25) =>
  useQuery({
    queryKey: smsTemplateKeys.list(page, perPage),
    queryFn: () => smsTemplatesApi.list(page, perPage)
  });

export const useSmsVariables = (subjectType = "lead") =>
  useQuery({
    queryKey: smsTemplateKeys.variables(subjectType),
    queryFn: () => smsTemplatesApi.variables(subjectType)
  });

export const useCreateSmsTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["SMS_TEMPLATES", "CREATE"],
    mutationFn: (payload: SmsTemplatePayload) =>
      smsTemplatesApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: smsTemplateKeys.lists() });
    }
  });
};

export const useUpdateSmsTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["SMS_TEMPLATES", "UPDATE"],
    mutationFn: ({
      id,
      payload
    }: {
      id: number;
      payload: Partial<SmsTemplatePayload>;
    }) => smsTemplatesApi.update(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: smsTemplateKeys.lists() });
    }
  });
};

export const useDeleteSmsTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["SMS_TEMPLATES", "DELETE"],
    mutationFn: (id: number) => smsTemplatesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: smsTemplateKeys.lists() });
    }
  });
};

// Phone Numbers

export const usePhoneNumbers = (enabled: boolean = true) =>
  useQuery({
    queryKey: phoneNumberKeys.lists(),
    queryFn: () => phoneNumbersApi.list(),
    enabled
  });

export const useRequestPhoneNumber = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload?: RequestPhoneNumberRequest) =>
      phoneNumbersApi.request(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: phoneNumberKeys.lists() });
    }
  });
};

export const useSetPhoneDefault = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => phoneNumbersApi.setDefault(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: phoneNumberKeys.lists() });
    }
  });
};

export const useRemovePhoneNumber = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => phoneNumbersApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: phoneNumberKeys.lists() });
    }
  });
};

export const usePropertyPhoneDefaults = (enabled: boolean = true) =>
  useQuery({
    queryKey: phoneNumberKeys.propertyDefaults(),
    queryFn: () => phoneNumbersApi.listPropertyDefaults(),
    enabled
  });

export const usePropertyPhoneDefault = (
  propertyId: number,
  enabled: boolean = true
) =>
  useQuery({
    queryKey: phoneNumberKeys.propertyDefault(propertyId),
    queryFn: () => phoneNumbersApi.getPropertyDefault(propertyId),
    enabled
  });

export const useSetPropertyPhoneDefault = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      propertyId,
      phoneNumberId
    }: {
      propertyId: number;
      phoneNumberId: string;
    }) => phoneNumbersApi.setPropertyDefault(propertyId, phoneNumberId),
    onSuccess: (_data, { propertyId }) => {
      queryClient.invalidateQueries({
        queryKey: phoneNumberKeys.propertyDefaults()
      });
      queryClient.invalidateQueries({
        queryKey: phoneNumberKeys.propertyDefault(propertyId)
      });
    }
  });
};

// Email Templates

const emailTemplateKeys = {
  all: ["EMAIL_TEMPLATES"],
  lists: () => [...emailTemplateKeys.all, "LIST"],
  list: (params?: Record<string, unknown>) => [
    ...emailTemplateKeys.lists(),
    params
  ],
  details: () => [...emailTemplateKeys.all, "DETAIL"],
  detail: (id: number) => [...emailTemplateKeys.details(), id],
  variables: (subjectType: string) => [
    ...emailTemplateKeys.all,
    "VARIABLES",
    subjectType
  ]
};

export const useEmailTemplates = (params?: Record<string, unknown>) =>
  useQuery({
    queryKey: emailTemplateKeys.list(params),
    queryFn: () => emailTemplatesApi.list(params)
  });

export const useEmailTemplate = (id: number) =>
  useQuery({
    queryKey: emailTemplateKeys.detail(id),
    queryFn: () => emailTemplatesApi.get(id),
    enabled: id > 0
  });

export const useEmailVariables = (subjectType = "lead") =>
  useQuery({
    queryKey: emailTemplateKeys.variables(subjectType),
    queryFn: () => emailTemplatesApi.variables(subjectType),
    staleTime: Infinity
  });

export const useCreateEmailTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [...emailTemplateKeys.all, "CREATE"],
    mutationFn: (payload: CreateEmailTemplatePayload) =>
      emailTemplatesApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: emailTemplateKeys.lists()
      });
      toast.success("Email template created");
    },
    onError: () => {
      toast.error("Failed to create email template");
    }
  });
};

export const useUpdateEmailTemplate = (id: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [...emailTemplateKeys.all, "UPDATE"],
    mutationFn: (payload: UpdateEmailTemplatePayload) =>
      emailTemplatesApi.update(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: emailTemplateKeys.lists()
      });
      queryClient.invalidateQueries({
        queryKey: emailTemplateKeys.detail(id)
      });
      toast.success("Email template updated");
    },
    onError: () => {
      toast.error("Failed to update email template");
    }
  });
};

export const useSendTestEmail = (id: number) =>
  useMutation({
    mutationKey: [...emailTemplateKeys.all, "SEND_TEST"],
    mutationFn: (emails: string[]) =>
      emailTemplatesApi.sendTest(id, { emails }),
    onSuccess: () => {
      toast.success("Test email sent");
    },
    onError: () => {
      toast.error("Failed to send test email");
    }
  });

export const useDeleteEmailTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [...emailTemplateKeys.all, "DELETE"],
    mutationFn: (id: number) => emailTemplatesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: emailTemplateKeys.lists()
      });
      toast.success("Email template deleted");
    },
    onError: () => {
      toast.error("Failed to delete email template");
    }
  });
};
